import { Modal } from "react-bootstrap";
import { useForm, Controller } from "react-hook-form";
import { errorToast, successToast } from "../../Toasts/Toasts";
import { addAggregatorTransaction } from "../../../services/supplierService";
import localStorageStore from "../../../utils/localStorageStore";
import { aggregatorTransactionTypes } from "../../../utils/constants";
import { useEffect, useRef } from "react";

const AddTransactionModal = ({
  showAddTransaction,
  setShowAddTransaction,
  supplierId,
  onSuccess,
  supplierData
}) => {
  const transactionTypes = [
    { value: 'cheque', label: 'Cheque' },
    { value: 'cash', label: 'Cash' },
    { value: 'bank_transfer', label: 'Bank Transfer' }
  ];
  const activeBank = supplierData?.BankAccounts?.find(bank => bank.status === true);
  const hasPrefilledId = useRef(false);
  const {
    handleSubmit,
    control,
    register,
    formState: { errors },
    setValue,
    watch,
  } = useForm();

  const usdAmountValue = watch("usdAmount");
  const exchangeRateValue = watch("exchangeRate");
  const commissionValue = watch("commission");
  const localAmountValue = watch("localAmount");

  useEffect(() => {
    if (usdAmountValue && exchangeRateValue && commissionValue) {
      let calculated = parseFloat(usdAmountValue) * parseFloat(exchangeRateValue);
      if (commissionValue && !isNaN(parseFloat(commissionValue))) {
        calculated += calculated * (parseFloat(commissionValue) / 100);
      }
      setValue("localAmount", isNaN(calculated) ? '' : calculated.toFixed(2));
    } else {
      setValue("localAmount", '');
    }
  }, [usdAmountValue, exchangeRateValue, commissionValue, setValue]);

  useEffect(() => {
    if (!showAddTransaction) {
      hasPrefilledId.current = false;
      return;
    }

    if (!hasPrefilledId.current) {
      setValue("id", supplierData?.AggregatorTransactions?.length + 1 ?? 1);
      hasPrefilledId.current = true;
    }
  }, [showAddTransaction, supplierData?.AggregatorTransactions?.length, setValue]);

  const handleClose = () => setShowAddTransaction(false);

  const onSubmit = async (data, event) => {
    const button = event.nativeEvent.submitter;
    button.disabled = true;
    const userId = parseInt(localStorageStore.getUserId());

    if (data?.type !== "cash" && (!activeBank?.bank_name || !activeBank?.account_number)) {
      errorToast("Bank details are required for non-cash transactions.");
      button.disabled = false;
      return;
    }
    
    let transactionInfo = {
      date: data?.date,
      type: data?.type,
      amount_usd: data?.usdAmount,
      local_currency: supplierData?.currency,
      exchange_rate: data?.exchangeRate,
      commission: data?.commission || 0,
      amount_local: data?.localAmount,
      remarks: data?.remarks,
      aggregator_id: supplierId,
      created_by_id: userId,
    };

    try {
      const response = await addAggregatorTransaction(transactionInfo);
      button.disabled = false;

      if (response?.success) {
        successToast(response?.message || "Transaction Added Successfully.");
        if (onSuccess) onSuccess();
        setShowAddTransaction(false);
      } else {
        errorToast(response?.message || "Failed to add transaction.");
        setShowAddTransaction(true);
      }
    } catch (error) {
      errorToast(error?.response?.data?.message || "An error occurred while adding the transaction.");
      setShowAddTransaction(true);
      button.disabled = false;
    }
  };

  return (
     <Modal show={showAddTransaction} size="md" centered>
      <div className="modal-simple modal-edit-user">
        <div className="modal-content p-3">
          <Modal.Body className="p-2">
            <form onSubmit={handleSubmit(onSubmit)}>
              <button type="button" className="btn-close" onClick={handleClose} />
              <div className="text-left mb-4">
                <h3 className="mb-2">Add Transaction</h3>
              </div>
              <div className="row g-3">
                {/* <div className="col-lg-6">
                  <label htmlFor="transaction-id" className="form-label">ID</label>
                  <input id="transaction-id" type="text" className="form-control" {...register("id")} disabled />
                </div> */}
                <div className="col-lg-12">
                  <label htmlFor="transaction-date" className="form-label">Date <sup className="text-danger">*</sup></label>
                  <input id="transaction-date" type="date" className="form-control" {...register("date", { required: "Date is required" })} max={new Date().toISOString().split("T")[0]} />
                  <small className="text-danger">{errors?.date && errors?.date?.message}</small>
                </div>
                <div className="col-lg-4">
                  <label htmlFor="transaction-type" className="form-label">Type <sup className="text-danger">*</sup></label>
                  <Controller
                    name="type"
                    control={control}
                    rules={{ required: "Type is required" }}
                    render={({ field }) => (
                      <>
                        <select id="transaction-type" className="form-select" {...field}>
                          <option value="">...</option>
                          {aggregatorTransactionTypes.map((type) => (
                            <option key={type.value} value={type.value}>
                              {type.label}
                            </option>
                          ))}
                        </select>
                        {errors?.type && <small className="text-danger">{errors?.type?.message}</small>}
                      </>
                    )}
                  />
                </div>
                <div className="col-lg-5">
                  <label htmlFor="transaction-usd-amount" className="form-label">Amount <sup className="text-danger">*</sup></label>
                  <div className="row g-2">
                    <div className="col-4">
                      <input type="text" value="USD" className="form-control" readOnly />
                    </div>
                    <div className="col-8">
                      <input id="transaction-usd-amount" type="number" className="form-control" min={0} step={1} {...register("usdAmount", { required: "Amount is required", validate: value => Number.isInteger(Number(value)) || "Amount must be a whole number" })} />
                      <small className="text-danger">{errors?.usdAmount && errors?.usdAmount?.message}</small>
                    </div>
                  </div>
                </div>
                <div className="col-lg-3">
                  <label htmlFor="transaction-commission" className="form-label">Comm. (%) <sup className="text-danger">*</sup></label>
                  <input id="transaction-commission" type="number" className="form-control" min={0} step="0.01" {...register("commission", { required: "Commission is required", validate: { decimalPlaces: value => { if (!value) return true; const decimalPart = value.toString().split('.')[1]; return !decimalPart || decimalPart.length <= 2 || "Commission cannot have more than 2 decimal places"; } } })} />
                  <small className="text-danger">{errors?.commission && errors?.commission?.message}</small>
                </div>
                <div className="col-lg-3">
                  <label htmlFor="transaction-supplier-currency" className="form-label">Supplier Currency</label>
                  <input id="transaction-supplier-currency" type="text" className="form-control" value={supplierData?.currency} readOnly />
                </div>
                <div className="col-lg-3">
                  <label htmlFor="transaction-exchange-rate" className="form-label">Exchange Rate <sup className="text-danger">*</sup></label>
                  <input id="transaction-exchange-rate" type="number" className="form-control" min={0} step="0.0001" {...register("exchangeRate", { required: "Exchange Rate is required", validate: { decimalPlaces: value => { const decimalPart = value.toString().split('.')[1]; return !decimalPart || decimalPart.length <= 4 || "Exchange Rate cannot have more than 4 decimal places"; } } })} />
                  <small className="text-danger">{errors?.exchangeRate && errors?.exchangeRate?.message}</small>
                </div>
                <div className="col-lg-6">
                  <label htmlFor="transaction-local-amount" className="form-label">Amount</label>
                  <div className="row g-2">
                    <div className="col-4">
                      <input id="transaction-local-currency" type="text" value={supplierData?.currency || ""} className="form-control" readOnly />
                    </div>
                    <div className="col-8">
                      <input id="transaction-local-amount" type="number" className="form-control" min={0} value={localAmountValue || ''} disabled {...register("localAmount")} />
                    </div>
                  </div>
                </div>
                <div className="col-lg-12">
                  <label htmlFor="transaction-remarks" className="form-label">Remarks</label>
                  <textarea id="transaction-remarks" className="form-control" rows={2} {...register("remarks")}></textarea>
                </div>
              </div>
              <div className="row mt-4">
                <p className="mb-2">Supplier Account <span className="text-muted">(for this transaction)</span> </p>
                <div className="bg-lighter px-3 py-2 rounded me-auto d-flex">
                  <div className="me-5">
                    <p className="mb-0">{activeBank?.bank_name}</p>
                    <span className="text-body">Bank Name</span>
                  </div>
                  <div className="me-0">
                    <p className="mb-0">{activeBank?.account_number
                      }</p>
                    <span className="text-body">Account No.</span>
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col-12 text-center mt-4">
                  <button type="submit" className="btn btn-primary rounded-pill me-sm-3 me-1 waves-effect waves-light" id="saveButton">Submit</button>
                  <button type="button" className="btn btn-label-secondary rounded-pill btn-reset waves-effect" id="cancelButton" onClick={handleClose}>Cancel</button>
                </div>
              </div>
            </form>
          </Modal.Body>
        </div>
      </div>
    </Modal>
  );
};

export default AddTransactionModal;
